import { ClientMethodsTypes, client } from '@/services/v2/client'

import { getGoogleConnectedStatus } from '@/services/oAuth'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useProfile } from 'containers/profile/hooks/useProfile'
import { ProfileQueryKeys } from 'containers/profile/types/query-keys'
import { useUser } from 'containers/user/hooks/useUser'
import { User, UserQueryKeys } from 'containers/user/types/user'
import { useMemo } from 'react'
import {
  DEFAULT_CONNECTIONS_QUERY_PARAMS,
  cleanProfilesWithoutId,
} from '../misc/connections'
import {
  ConnectionsQueryParams,
  ConnectionsResponseDTO,
} from '../types/connections'
import { useCleanConnection } from './useConnectionsActions'

const BASE_URL = '/api/v1/users'

export const useConnections = (queryParams?: ConnectionsQueryParams) => {
  const { profile } = useProfile()

  const query = queryParams || DEFAULT_CONNECTIONS_QUERY_PARAMS

  const { data, ...rest } = useQuery({
    queryFn: () =>
      client<ConnectionsResponseDTO>(
        `${BASE_URL}/connections/${profile?.id}/${query.page}${
          query?.searchQuery ? `?searchQuery=${query.searchQuery}` : ''
        }`
      ),
    queryKey: [
      UserQueryKeys.CONNECTIONS,
      ProfileQueryKeys.CONNECTIONS,
      profile?.id,
      query.page,
      query?.searchQuery,
    ],
    enabled: !!profile?.id,
  })

  return {
    connections: cleanProfilesWithoutId(data?.connections || []),
    numberOfResults: data?.numberOfResults || 0,
    ...rest,
  }
}

export const useNewConnections = () => {
  const { user } = useUser()

  const { data, ...rest } = useQuery({
    queryFn: () => client<User[]>(`${BASE_URL}/connections/new-connections`),
    queryKey: [
      UserQueryKeys.CONNECTIONS,
      UserQueryKeys.NEW_CONNECTIONS,
      user?.id,
    ],
    enabled: !!user?.id,
  })

  const newConnections = useMemo(
    () => cleanProfilesWithoutId(data || []),
    [data]
  )

  return {
    newConnections,
    newConnectionsCount: newConnections.length,
    ...rest,
  }
}

export const useRemoveNewConnection = () => {
  const cleanUserConnection = useCleanConnection()

  return useMutation({
    mutationFn: (userId: string) =>
      client(
        `${BASE_URL}/connections/new-connections?connectionToDismiss=${userId}`,
        {
          method: ClientMethodsTypes.DELETE,
        }
      ),
    onSuccess: cleanUserConnection,
  })
}

export const useInvitationsReceived = () => {
  const { user } = useUser()

  const ids = useMemo(() => {
    return (
      user?.connectionsReceived?.map((invitation) => {
        // if invitations is string because is an old invitation
        if (typeof invitation === 'string') return invitation
        // else return the userId which uses the new invitation format
        return invitation.userId
      }) || []
    )
  }, [user])

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      UserQueryKeys.CONNECTIONS,
      UserQueryKeys.INVITATIONS,
      user?.id,
      ids,
    ],
    queryFn: () =>
      client<User[]>(`${BASE_URL}/retrieve-users`, {
        method: ClientMethodsTypes.POST,
        body: ids,
      }),
    enabled: !!user?.id && ids.length > 0,
    retry: false,
  })

  return {
    invitations: data && ids?.length > 0 ? data : [],
    isEmpty: ids?.length === 0,
    invitationsCount: data?.length || 0,
    isLoading,
    refetch,
    error,
  }
}

export const useGoogleConnectedStatus = () => {
  const { user } = useUser()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [UserQueryKeys.CONNECTIONS, 'gmail-connection-status', user?.id],
    queryFn: () => getGoogleConnectedStatus(),
    enabled: !!user?.id,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchIntervalInBackground: true,
    staleTime: 0,
    cacheTime: 0,
  })

  return {
    isGmailConnected: data?.gmailConnected,
    isLoading,
    error,
    refetch,
    user,
    // Helper properties
    isReady: !isLoading && !error,
    hasError: !!error,
  }
}
